import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus, MapPin, ShoppingBag, Repeat, Ticket, ArrowRight, Check, CheckCircle2,
  Sparkles, PenTool, Megaphone, BarChart3, MessageSquare, ShieldCheck, Wand2, X, Loader2, Store, Target, Radio,
  Users, Layers, Search, Bot, Tv, Presentation, Video, ShoppingCart, LineChart,
} from "lucide-react";
import { MarketingPage } from "../../components/smartreach/Chrome";
import { sr } from "../../lib/sr";

const GOAL_KEY = "sr_home_goal";

const GOALS = [
  { key: "more_inquiries", label: "More inquiries", icon: UserPlus, d: "Turn nearby interest into calls, forms and messages." },
  { key: "more_store_visits", label: "More store visits", icon: MapPin, d: "Bring more of your local area through the door." },
  { key: "more_sales", label: "More sales", icon: ShoppingBag, d: "Drive purchases with the right offer at the right time." },
  { key: "more_repeat_business", label: "More repeat business", icon: Repeat, d: "Keep customers coming back with timely follow-ups." },
  { key: "promote_an_event", label: "Promote an event", icon: Ticket, d: "Fill seats or boost turnout for a date that matters." },
];
const GOAL_LABEL = Object.fromEntries(GOALS.map((g) => [g.key, g.label]));

const STEPS = [
  { t: "Tell us about your business", d: "Share your location, goal and budget range." },
  { t: "Review your campaign plan", d: "See recommended services, creative, costs and the support you selected." },
  { t: "Launch and track results", d: "Approve the work and review delivery and available business outcomes." },
];

const WORKS = [
  { icon: PenTool, t: "Plan and create", d: "Growth planning, offers, ad copy and creative tools.", to: "/how-it-works" },
  { icon: Megaphone, t: "Reach your audience", d: "Meta & Google Ads, ChatGPT Ads, Connected TV, digital billboards, eligible SmartReach inventory, competitor-location audiences and radio where available.", to: "/competitor-targeting" },
  { icon: MessageSquare, t: "Handle the response", d: "Advertiser lead intake, replies and offer redemption supported by actual integrations.", to: "/professional-service" },
  { icon: BarChart3, t: "Understand results", d: "Media spend, leads, redemptions and matched purchase revenue where connected, with measurement qualifications.", to: "/visit-intelligence" },
];

// Full channel lineup now coordinated inside one plan. `tag` surfaces newest additions.
const CHANNELS = [
  { icon: Megaphone, slug: "meta", t: "Meta Ads", d: "Facebook & Instagram, planned and managed for you." },
  { icon: Search, slug: "google", t: "Google Ads", d: "Capture high-intent local searches as they happen." },
  { icon: Bot, slug: "chatgpt", t: "ChatGPT Ads", d: "Be present in AI chat answers where people now research.", tag: "Early access", to: "/channels/chatgpt-ads" },
  { icon: Tv, slug: "ctv", t: "Connected TV", d: "Streaming-TV spots targeted to your local area." },
  { icon: Layers, slug: "display", t: "Digital display", d: "Run across eligible SmartReach display inventory." },
  { icon: Presentation, slug: "dooh", t: "Digital billboards", d: "Programmatic out-of-home screens around your neighborhood.", tag: "New", to: "/channels/digital-billboards" },
  { icon: Radio, slug: "radio", t: "Local radio", d: "Community radio flights where available in your market." },
  { icon: Ticket, slug: "offers", t: "Local offers & coupons", d: "First-visit offers customers redeem in your store." },
];

// AI + measurement capabilities added since launch.
const CAPABILITIES = [
  { icon: Sparkles, slug: "growth-plans", t: "AI Growth Plans", d: "Answer a few questions at signup and get a plan tailored to your industry — 17 verticals, each with its own playbook.", tag: "New" },
  { icon: Video, slug: "creative", t: "AI creative studio", d: "Generate ad copy, images and AI-presenter videos — with voice and aspect-ratio options for every placement.", tag: "New" },
  { icon: ShoppingCart, slug: "dynamic-ads", t: "Dynamic Product Ads", d: "Auto-build ads straight from your product catalog and keep them in step with what's in stock.", tag: "New" },
  { icon: LineChart, slug: "verified-sales", t: "Verified Sales reporting", d: "Connect Shopify to match real purchases back to the campaigns that drove them.", tag: "New" },
];

const LEVELS = [
  { t: "Self-Service", d: "Use the available planning and creative tools and manage your own supported campaigns." },
  { t: "AI-Assisted + Expert Review", d: "Prepare work with AI and obtain the expert review included in the selected service.", rec: true },
  { t: "Professional Service", d: "Have the team handle the work listed in your service agreement, with customer approvals where required." },
];

const EVIDENCE = [
  ["Projected", "A forecast based on stated assumptions."],
  ["Observed", "A recorded event from an identified source — not automatically a verified sale."],
  ["Modeled", "A statistical estimate, with its method and limits."],
  ["Verified purchase", "A purchase matched to a transaction record (refunds handled) — not proof the ad caused it."],
];

const BUDGETS = ["Not sure yet", "Under $500 / mo", "$500–$1,500 / mo", "$1,500–$5,000 / mo", "$5,000+ / mo"];

const SecHead = ({ eyebrow, title, sub }) => (
  <div className="srx-head">
    <span className="srx-eyebrow">{eyebrow}</span>
    <h2>{title}</h2>
    {sub && <p>{sub}</p>}
  </div>
);

const SampleTag = ({ children }) => (
  <span style={{
    fontSize: 10, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase",
    color: "var(--x-amber-dark)", background: "rgba(242,163,61,.14)", border: "1px solid rgba(242,163,61,.4)",
    borderRadius: 999, padding: "2px 8px",
  }}>{children}</span>
);

// ---------------- Inquiry modal (feeds the platform Sales CRM) ----------------
function InquiryModal({ open, onClose, goal, setGoal }) {
  const empty = { name: "", business: "", email: "", city: "", phone: "", website: "", budget_range: "", preferred: "", message: "", hp: "", ack: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (open) { setDone(false); setF(empty); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.business || !f.email || !f.city) { toast.error("Please fill your name, business, email and city/ZIP."); return; }
    if (!f.ack) { toast.error("Please acknowledge the privacy note to continue."); return; }
    setBusy(true);
    try {
      const goalLabel = GOAL_LABEL[goal] || "General inquiry";
      const parts = [`Goal: ${goalLabel}`];
      if (f.preferred) parts.push(`Prefers ${f.preferred}`);
      if (f.message) parts.push(f.message);
      await sr.salesInquiry({
        form: "marketing_plan", name: f.name, business: f.business, email: f.email,
        city: f.city, phone: f.phone || null, website: f.website || null,
        budget_range: f.budget_range || null, interests: goal ? [goal] : null,
        message: parts.join(" · "), source_page: "home", hp: f.hp,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again shortly.");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="Request my marketing plan" data-testid="inquiry-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(6,10,20,.72)", backdropFilter: "blur(4px)", zIndex: 10000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto" }}>
      <div style={{ maxWidth: 560, width: "100%", background: "var(--x-panel, #101826)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 24, marginTop: "5vh", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
          <div>
            <span className="srx-eyebrow" style={{ marginBottom: 6, display: "block", color: "var(--x-amber)" }}>Request my marketing plan</span>
            {!done && <p style={{ color: "var(--x-muted-dark)", fontSize: 14, margin: 0 }}>Tell us about your business. Our team will review your goals and follow up.</p>}
          </div>
          <button onClick={onClose} data-testid="inquiry-close" aria-label="Close"
            style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: 6, lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div data-testid="inquiry-success" style={{ textAlign: "center", padding: "28px 8px 12px" }}>
            <CheckCircle2 size={46} color="#3fbf7f" style={{ marginBottom: 14 }} />
            <h3 style={{ color: "#fff", fontSize: 22, marginBottom: 8 }}>Request received</h3>
            <p style={{ color: "var(--x-muted-dark)", fontSize: 15, maxWidth: 400, margin: "0 auto 20px" }}>
              Thanks — our team will review your goals and follow up. No payment or account is needed to get your plan started.
            </p>
            <button className="srx-btn-primary" data-testid="inquiry-success-close" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 16, display: "grid", gap: 12 }}>
            {/* honeypot */}
            <input type="text" value={f.hp} onChange={up("hp")} tabIndex={-1} autoComplete="off"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

            <label style={lbl}>What do you want to grow?
              <select value={goal || ""} onChange={(e) => setGoal(e.target.value || null)} data-testid="inquiry-goal" style={inp}>
                <option value="">General inquiry</option>
                {GOALS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </label>
            <div style={row2}>
              <label style={lbl}>Your name*<input value={f.name} onChange={up("name")} data-testid="inquiry-name" style={inp} placeholder="Jane Smith" /></label>
              <label style={lbl}>Business name*<input value={f.business} onChange={up("business")} data-testid="inquiry-business" style={inp} placeholder="Sunrise Bistro" /></label>
            </div>
            <div style={row2}>
              <label style={lbl}>Email*<input type="email" value={f.email} onChange={up("email")} data-testid="inquiry-email" style={inp} placeholder="you@business.com" /></label>
              <label style={lbl}>City / ZIP*<input value={f.city} onChange={up("city")} data-testid="inquiry-city" style={inp} placeholder="Dallas, TX / 75201" /></label>
            </div>
            <div style={row2}>
              <label style={lbl}>Phone <span style={opt}>(optional)</span><input value={f.phone} onChange={up("phone")} data-testid="inquiry-phone" style={inp} placeholder="(555) 123-4567" /></label>
              <label style={lbl}>Website <span style={opt}>(optional)</span><input value={f.website} onChange={up("website")} data-testid="inquiry-website" style={inp} placeholder="business.com" /></label>
            </div>
            <div style={row2}>
              <label style={lbl}>Monthly budget <span style={opt}>(optional)</span>
                <select value={f.budget_range} onChange={up("budget_range")} data-testid="inquiry-budget" style={inp}>
                  <option value="">Select a range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label style={lbl}>Preferred contact <span style={opt}>(optional)</span>
                <select value={f.preferred} onChange={up("preferred")} data-testid="inquiry-preferred" style={inp}>
                  <option value="">No preference</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
              </label>
            </div>
            <label style={lbl}>Anything else? <span style={opt}>(optional)</span>
              <textarea value={f.message} onChange={up("message")} data-testid="inquiry-message" rows={3} style={{ ...inp, resize: "vertical" }} placeholder="Tell us about your goal, timing or competitors." />
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: "var(--x-muted-dark)" }}>
              <input type="checkbox" checked={f.ack} onChange={(e) => setF((s) => ({ ...s, ack: e.target.checked }))} data-testid="inquiry-ack" style={{ marginTop: 3 }} />
              <span>I understand SmartReach will use these details to review my goals and follow up. This is an inquiry, not an order — no payment or account is created.</span>
            </label>
            <button type="submit" className="srx-btn-primary" data-testid="inquiry-submit" disabled={busy} style={{ justifyContent: "center", width: "100%" }}>
              {busy ? <><Loader2 size={16} className="sr-spin" /> Sending…</> : <>Send my request <ArrowRight size={16} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const lbl = { display: "grid", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#fff" };
const opt = { fontWeight: 500, color: "var(--x-muted-dark)" };
const inp = { background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 9, padding: "10px 12px", color: "#fff", fontSize: 14, fontWeight: 500, width: "100%" };
const row2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

// ---------------- Shopify online-store success story ----------------
function ShopifyCaseModal({ open, onClose, onCta }) {
  if (!open) return null;
  const stats = [
    { v: "2.1× → 4.3×", k: "Return on ad spend", c: "#3fbf7f" },
    { v: "+38%", k: "Online revenue (90 days)", c: "#3fbf7f" },
    { v: "−27%", k: "Cost to acquire a customer", c: "var(--x-amber)" },
    { v: "+$14,200", k: "Recovered abandoned carts", c: "#3fbf7f" },
  ];
  const ran = [
    "Shopify catalog synced into dynamic product ads that update as stock changes",
    "Retargeting for browsers and cart-abandoners across social and display",
    "Verified-sales attribution tying ad spend back to real Shopify orders",
    "Automated email & SMS win-back for lapsed customers",
  ];
  return (
    <div role="dialog" aria-modal="true" aria-label="Online store success story" data-testid="shopify-case-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(6,10,20,.72)", backdropFilter: "blur(4px)", zIndex: 10000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto" }}>
      <div style={{ maxWidth: 560, width: "100%", background: "var(--x-panel, #101826)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 24, marginTop: "5vh", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <span className="srx-eyebrow" style={{ marginBottom: 6, display: "block", color: "var(--x-amber)" }}>Online store success story</span>
            <h3 style={{ color: "#fff", fontSize: 22, margin: 0, display: "flex", alignItems: "center", gap: 8 }}><ShoppingCart size={20} color="var(--x-amber)" /> Harbor Threads · Shopify</h3>
          </div>
          <button onClick={onClose} data-testid="shopify-case-close" aria-label="Close"
            style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: 6, lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 14.5, lineHeight: 1.6, margin: "14px 0 0" }}>
          A coastal apparel brand selling on Shopify was watching ad costs climb while repeat purchases stalled.
          We connected their store, let AI build and refresh product ads, and tracked every sale back to the spend
          that drove it. Over the next quarter the numbers moved:
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "16px 0" }} data-testid="shopify-case-stats">
          {stats.map((s) => (
            <div key={s.k} style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, padding: "14px 14px" }}>
              <div style={{ color: s.c, fontSize: 22, fontWeight: 800 }}>{s.v}</div>
              <div style={{ color: "var(--x-muted-dark)", fontSize: 12, marginTop: 3 }}>{s.k}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: "var(--x-muted-dark)", textTransform: "uppercase", letterSpacing: 2, marginBottom: 6 }}>What we ran</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 13.5, lineHeight: 1.75 }}>
          {ran.map((r) => <li key={r}>{r}</li>)}
        </ul>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 11.5, margin: "14px 0 16px" }}>
          Representative 90-day scenario — figures illustrate the kind of results we work toward, not a guarantee.
        </p>

        <button className="srx-btn-primary" data-testid="shopify-case-cta" onClick={onCta} style={{ justifyContent: "center", width: "100%" }}>
          Grow my online store <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------------- Movie-distributor success story ----------------
function MovieCaseModal({ open, onClose, onCta }) {
  if (!open) return null;
  const stats = [
    { v: "+31%", k: "Opening-weekend tickets vs prior release", c: "#3fbf7f" },
    { v: "1.2M → 2.4M", k: "Trailer views in 2 weeks", c: "#3fbf7f" },
    { v: "3.8M", k: "DOOH impressions near theaters (6 markets)", c: "var(--x-amber)" },
    { v: "−22%", k: "In-target cost per 1,000 (CPM)", c: "#3fbf7f" },
  ];
  const ran = [
    "Live Buzz Radar tracking real YouTube trailer views, likes and comments",
    "Digital billboards (DOOH) near theaters across release markets via your ad platform",
    "YouTube trailer promotion + retargeting of trailer-viewers for opening week",
    "Moviegoer audience targeting and a market-by-market budget split",
  ];
  return (
    <div role="dialog" aria-modal="true" aria-label="Movie distributor success story" data-testid="movie-case-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(6,10,20,.72)", backdropFilter: "blur(4px)", zIndex: 10000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto" }}>
      <div style={{ maxWidth: 560, width: "100%", background: "var(--x-panel, #101826)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 24, marginTop: "5vh", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <span className="srx-eyebrow" style={{ marginBottom: 6, display: "block", color: "var(--x-amber)" }}>Movie distributor success story</span>
            <h3 style={{ color: "#fff", fontSize: 22, margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Video size={20} color="var(--x-amber)" /> "Midnight in Mumbai" launch</h3>
          </div>
          <button onClick={onClose} data-testid="movie-case-close" aria-label="Close"
            style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: 6, lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 14.5, lineHeight: 1.6, margin: "14px 0 0" }}>
          A regional distributor opening a thriller in six markets needed opening-weekend turnout. We tracked the
          film's live buzz, pushed the trailer where fans already were, and put the poster on screens near theaters.
          Through release week:
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "16px 0" }} data-testid="movie-case-stats">
          {stats.map((s) => (
            <div key={s.k} style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, padding: "14px 14px" }}>
              <div style={{ color: s.c, fontSize: 21, fontWeight: 800 }}>{s.v}</div>
              <div style={{ color: "var(--x-muted-dark)", fontSize: 12, marginTop: 3 }}>{s.k}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: "var(--x-muted-dark)", textTransform: "uppercase", letterSpacing: 2, marginBottom: 6 }}>What we ran</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 13.5, lineHeight: 1.75 }}>
          {ran.map((r) => <li key={r}>{r}</li>)}
        </ul>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 11.5, margin: "14px 0 16px" }}>
          Representative launch — figures illustrate the kind of results we work toward, not a guarantee.
        </p>

        <button className="srx-btn-primary" data-testid="movie-case-cta" onClick={onCta} style={{ justifyContent: "center", width: "100%" }}>
          Plan my film launch <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------------- Local restaurant success story ----------------
function RestaurantCaseModal({ open, onClose, onCta }) {
  if (!open) return null;
  const stats = [
    { v: "+22%", k: "Weekday lunch covers", c: "#3fbf7f" },
    { v: "1,900", k: "Offer redemptions / quarter", c: "#3fbf7f" },
    { v: "4.6★", k: "Rating after +240 new reviews", c: "var(--x-amber)" },
    { v: "−18%", k: "Cost per tracked visit", c: "#3fbf7f" },
  ];
  const ran = [
    "Local offers running across search, social and maps in the neighborhoods that matter",
    "Competitor-area reach so nearby diners see the offer when they're choosing lunch",
    "An AI review responder keeping replies fast and on-brand",
    "A weekday-lunch promotion with redemptions tracked back to spend",
  ];
  return (
    <div role="dialog" aria-modal="true" aria-label="Local restaurant success story" data-testid="restaurant-case-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(6,10,20,.72)", backdropFilter: "blur(4px)", zIndex: 10000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto" }}>
      <div style={{ maxWidth: 560, width: "100%", background: "var(--x-panel, #101826)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 24, marginTop: "5vh", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <span className="srx-eyebrow" style={{ marginBottom: 6, display: "block", color: "var(--x-amber)" }}>Local restaurant success story</span>
            <h3 style={{ color: "#fff", fontSize: 22, margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Store size={20} color="var(--x-amber)" /> Sunrise Bistro</h3>
          </div>
          <button onClick={onClose} data-testid="restaurant-case-close" aria-label="Close"
            style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: 6, lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 14.5, lineHeight: 1.6, margin: "14px 0 0" }}>
          A neighborhood bistro wanted fuller weekday lunches without discounting its way to zero. We ran local offers
          where nearby diners already look, kept reviews fresh, and traced redemptions back to spend. Over a quarter:
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "16px 0" }} data-testid="restaurant-case-stats">
          {stats.map((s) => (
            <div key={s.k} style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, padding: "14px 14px" }}>
              <div style={{ color: s.c, fontSize: 21, fontWeight: 800 }}>{s.v}</div>
              <div style={{ color: "var(--x-muted-dark)", fontSize: 12, marginTop: 3 }}>{s.k}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: "var(--x-muted-dark)", textTransform: "uppercase", letterSpacing: 2, marginBottom: 6 }}>What we ran</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 13.5, lineHeight: 1.75 }}>
          {ran.map((r) => <li key={r}>{r}</li>)}
        </ul>

        <p style={{ color: "var(--x-muted-dark)", fontSize: 11.5, margin: "14px 0 16px" }}>
          Representative quarter — figures illustrate the kind of results we work toward, not a guarantee.
        </p>

        <button className="srx-btn-primary" data-testid="restaurant-case-cta" onClick={onCta} style={{ justifyContent: "center", width: "100%" }}>
          Fill my tables <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------------- Rotating success stories ----------------
const STORIES = [
  { key: "restaurant", eyebrow: "Local restaurant", title: "Sunrise Bistro", icon: Store, stat: "+22% weekday covers", line: "Filled weekday lunch tables with local offers, fresh reviews and competitor-area reach." },
  { key: "shopify", eyebrow: "Online store", title: "Harbor Threads · Shopify", icon: ShoppingCart, stat: "ROAS 2.1× → 4.3×", line: "Turned rising ad costs into profitable online sales with dynamic product ads." },
  { key: "movie", eyebrow: "Movie distributor", title: "\u201cMidnight in Mumbai\u201d", icon: Video, stat: "+31% opening tickets", line: "Filled opening weekend across six markets with Buzz Radar, trailer ads and DOOH." },
];

function StoriesSection({ onOpen }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setI((p) => (p + 1) % STORIES.length), 6000);
    return () => clearInterval(id);
  }, [paused]);
  const s = STORIES[i];
  return (
    <section className="srx-sec dark" data-testid="success-stories">
      <div className="srx-wrap">
        <SecHead eyebrow="Success stories"
          title="Real-world playbooks, one platform"
          sub="However you grow — a local storefront, an online store, or a movie release — here's the kind of result we work toward." />
        <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
          data-testid={`story-card-${s.key}`}
          style={{ maxWidth: 680, margin: "0 auto", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
          <span className="srx-eyebrow" style={{ color: "var(--x-amber)", display: "block", marginBottom: 10 }}>{s.eyebrow}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="srx-ico amber"><s.icon size={22} /></span>
            <div>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 20 }}>{s.title}</h3>
              <div style={{ color: "var(--x-muted-dark)", fontSize: 14, marginTop: 3 }}>{s.line}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
            <span style={{ color: "#3fbf7f", fontWeight: 800, fontSize: 22 }}>{s.stat}</span>
            <button className="srx-btn-primary" data-testid="story-read-btn" onClick={() => onOpen(s.key)}>Read the story <ArrowRight size={15} /></button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }} data-testid="story-dots">
          {STORIES.map((st, idx) => (
            <button key={st.key} onClick={() => setI(idx)} aria-label={`Show ${st.title} story`} data-testid={`story-dot-${st.key}`}
              style={{ width: idx === i ? 26 : 9, height: 9, borderRadius: 999, border: "none", cursor: "pointer", background: idx === i ? "var(--x-amber)" : "rgba(255,255,255,.3)", transition: "width .2s" }} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------- Page ----------------
export default function HomeV2() {
  const [modalOpen, setModalOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const [movieCaseOpen, setMovieCaseOpen] = useState(false);
  const [restaurantCaseOpen, setRestaurantCaseOpen] = useState(false);
  const openStory = (k) => (k === "restaurant" ? setRestaurantCaseOpen(true) : k === "shopify" ? setCaseOpen(true) : setMovieCaseOpen(true));
  const [goal, setGoalState] = useState(() => localStorage.getItem(GOAL_KEY) || null);
  const [pricing, setPricing] = useState(null);

  const setGoal = useCallback((g) => {
    setGoalState(g);
    if (g) localStorage.setItem(GOAL_KEY, g); else localStorage.removeItem(GOAL_KEY);
  }, []);

  useEffect(() => { sr.metaModulePricing().then(setPricing).catch(() => setPricing(null)); }, []);

  const openInquiry = (g) => { if (g !== undefined) setGoal(g); setModalOpen(true); };

  const moduleFee = pricing ? `$${pricing.module_fee.toFixed(2)}` : "$49.99";
  const mgmtPct = pricing ? pricing.management_rate_pct : 15;

  return (
    <MarketingPage>
      {/* A. HERO */}
      <section className="srx-hero" data-testid="hero">
        <div className="srx-wrap srx-hero-inner">
          <div className="srxv-hero-grid">
            <div className="srxv-hero-copy">
              <span className="srx-eyebrow" data-testid="hero-eyebrow">AI tools. Local, online &amp; on-screen media. Expert support.</span>
              <h1 data-testid="hero-headline">Your marketing, working together.</h1>
              <p className="srx-sub" style={{ margin: "0 0 26px", maxWidth: 580 }} data-testid="hero-body">
                One workspace to plan campaigns, create ads, manage inquiries and track results — whether you run a
                local storefront, an online store with Shopify built in, or a movie release. Start with AI, add
                expert help when you need it, and coordinate digital, radio, DOOH and local offers.
              </p>
              <div className="srx-cta-row" style={{ justifyContent: "flex-start" }}>
                <button className="srx-btn-primary" data-testid="hero-primary-btn" onClick={() => openInquiry()}>
                  Request my marketing plan
                </button>
                <Link to="/pricing" className="srx-btn-secondary" data-testid="hero-secondary-btn">View plans &amp; pricing</Link>
              </div>
              <p style={{ marginTop: 14, fontSize: 13, color: "var(--x-muted-dark)", maxWidth: 460 }} data-testid="hero-support-text">
                Tell us about your business. Our team will review your goals and follow up.
              </p>
            </div>
            {/* Sample workspace previews — local + online, so both audiences see themselves */}
            <div style={{ display: "grid", gap: 12 }}>
            <div className="srxv-hero-preview" data-testid="hero-preview" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 16, boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontWeight: 700, fontSize: 14 }}><Store size={16} color="var(--x-amber)" /> Sunrise Bistro</span>
                <SampleTag>Local workspace</SampleTag>
              </div>
              {[
                { t: "Weekday lunch offer", s: "Approved", c: "#3fbf7f" },
                { t: "Competitor-area reach", s: "Delivering", c: "var(--x-amber-dark)" },
                { t: "New review responder", s: "Needs attention", c: "#e88" },
              ].map((r) => (
                <div key={r.t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 12px", borderRadius: 10, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.08)", marginBottom: 8 }}>
                  <span style={{ color: "#e8eefc", fontSize: 13.5, fontWeight: 600 }}>{r.t}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: r.c }}>{r.s}</span>
                </div>
              ))}
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--x-muted-dark)", textAlign: "center" }}>Illustrative interface — not customer results.</div>
            </div>
            {/* Online store (Shopify) proof tile — opens a short success story */}
            <div data-testid="hero-online-proof" role="button" tabIndex={0} onClick={() => setCaseOpen(true)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCaseOpen(true); } }}
              style={{ cursor: "pointer", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 16, padding: 16, boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontWeight: 700, fontSize: 14 }}><ShoppingCart size={16} color="var(--x-amber)" /> Harbor Threads · Shopify</span>
                <SampleTag>Online store</SampleTag>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ k: "Catalog", v: "Synced" }, { k: "Dynamic ads", v: "Live" }, { k: "Verified sales", v: "Tracked" }].map((m) => (
                  <div key={m.k} style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.08)" }}>
                    <div style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>{m.v}</div>
                    <div style={{ color: "var(--x-muted-dark)", fontSize: 10.5, marginTop: 2 }}>{m.k}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--x-muted-dark)" }}>Shopify catalog, dynamic product ads &amp; verified-sales attribution — built in.</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--x-amber)", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }} data-testid="hero-online-proof-link">View story <ArrowRight size={13} /></span>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* B. CHOOSE THE RESULT YOU WANT */}
      <section className="srx-sec light" data-testid="goals">
        <div className="srx-wrap">
          <SecHead eyebrow="Start with your goal" title="Choose the result you want"
            sub="Pick a goal and we'll shape your plan around it. These are goals to work toward, not guaranteed results." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))" }}>
            {GOALS.map((g) => (
              <button key={g.key} className="srx-ccard" data-testid={`goal-${g.key}`} onClick={() => openInquiry(g.key)}
                style={{ textAlign: "left", cursor: "pointer", border: goal === g.key ? "1px solid var(--x-amber)" : undefined }}>
                <span className="srx-ico ink"><g.icon size={20} /></span>
                <h3>{g.label}</h3>
                <p>{g.d}</p>
                <span className="srx-learn">Request my plan <ArrowRight size={14} /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* C. THREE STEPS */}
      <section className="srx-sec dark" data-testid="steps">
        <div className="srx-wrap">
          <SecHead eyebrow="How it works" title="Three steps to get going" />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {STEPS.map((s, i) => (
              <div key={s.t} className="srx-ccard" data-testid={`step-${i + 1}`}>
                <span className="srx-stepnum">{i + 1}</span>
                <h3 style={{ marginTop: 12 }}>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 16, fontSize: 13.5, color: "var(--x-muted-dark)" }}>
            Want the full detail? <Link to="/how-it-works" className="srx-learn" data-testid="steps-detail-link" style={{ display: "inline-flex" }}>See how it works <ArrowRight size={14} /></Link>
          </p>
        </div>
      </section>

      {/* D. WHAT WORKS TOGETHER */}
      <section className="srx-sec light" data-testid="works">
        <div className="srx-wrap">
          <SecHead eyebrow="One workspace" title="What works together"
            sub="Everything connects — from planning to results — so you're not stitching tools together." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {WORKS.map((w) => (
              <Link key={w.t} to={w.to} className="srx-ccard" data-testid={`works-${w.t.split(" ")[0].toLowerCase()}`}>
                <span className="srx-ico ink"><w.icon size={20} /></span>
                <h3>{w.t}</h3>
                <p>{w.d}</p>
                <span className="srx-learn">Learn more <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* D1. EVERY CHANNEL, ONE PLAN */}
      <section className="srx-sec dark" data-testid="channels">
        <div className="srx-wrap">
          <SecHead eyebrow="Every channel, one plan"
            title="All your local advertising, coordinated"
            sub="Pick a goal and budget — we size the right mix across these channels and run them together, instead of juggling a separate login for each." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
            {CHANNELS.map((c) => {
              const body = (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <span className="srx-ico ink"><c.icon size={20} /></span>
                    {c.tag && <span className="srx-badge-pop" style={{ marginLeft: 0 }}>{c.tag}</span>}
                  </div>
                  <h3 style={{ marginTop: 12 }}>{c.t}{c.to && <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: "middle" }} />}</h3>
                  <p>{c.d}</p>
                </>
              );
              return c.to
                ? <Link key={c.slug} to={c.to} className="srx-ccard" data-testid={`channel-${c.slug}`} style={{ textDecoration: "none", borderColor: "var(--x-amber)" }}>{body}</Link>
                : <div key={c.slug} className="srx-ccard" data-testid={`channel-${c.slug}`} style={c.tag ? { borderColor: "var(--x-amber)" } : {}}>{body}</div>;
            })}
          </div>
          <p style={{ marginTop: 16, fontSize: 13.5, color: "var(--x-muted-dark)" }}>
            Channel availability depends on your area, budget and connected accounts. ChatGPT Ads are rolling out in early access.
          </p>
        </div>
      </section>

      {/* D1b. AI + MEASUREMENT CAPABILITIES */}
      <section className="srx-sec light" data-testid="capabilities">
        <div className="srx-wrap">
          <SecHead eyebrow="New in SmartReach"
            title="AI that plans, creates and measures"
            sub="More of the work done for you — from a plan built around your industry to creative you can run and sales you can actually trace back." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {CAPABILITIES.map((c) => (
              <div key={c.slug} className="srx-ccard" data-testid={`capability-${c.slug}`}
                style={{ borderColor: "var(--x-amber)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <span className="srx-ico amber"><c.icon size={20} /></span>
                  {c.tag && <span className="srx-badge-pop" style={{ marginLeft: 0 }}>{c.tag}</span>}
                </div>
                <h3 style={{ marginTop: 12 }}>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 16, fontSize: 13.5, color: "var(--x-muted, #64748b)" }}>
            Verified Sales matching requires a connected store (Shopify). Results always carry their evidence level — a matched purchase isn't proof the ad caused it.
          </p>
          <div data-testid="movie-case-tile" role="button" tabIndex={0} onClick={() => setMovieCaseOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMovieCaseOpen(true); } }}
            style={{ marginTop: 18, cursor: "pointer", border: "1px solid var(--x-amber)", borderRadius: 14, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, justifyContent: "space-between", flexWrap: "wrap", background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="srx-ico amber"><Video size={20} /></span>
              <div>
                <strong style={{ color: "var(--x-ink, #0f172a)", fontSize: 15 }}>Movie Marketing Studio</strong>
                <div style={{ fontSize: 13, color: "var(--x-muted, #64748b)" }}>Marketing a film release? See how a distributor filled opening weekend.</div>
              </div>
            </div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--x-amber)", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }} data-testid="movie-case-tile-link">See a distributor story <ArrowRight size={14} /></span>
          </div>
          <p style={{ marginTop: 10, fontSize: 13.5, color: "var(--x-muted, #64748b)" }}>
            Or <Link to="/solutions/movie-marketing" className="srx-learn" data-testid="capabilities-movie-link" style={{ display: "inline-flex" }}>explore the Movie Marketing Studio <ArrowRight size={14} /></Link>
          </p>
        </div>
      </section>

      {/* D1c. ROTATING SUCCESS STORIES */}
      <StoriesSection onOpen={openStory} />

      {/* D2. COMPETITOR CONQUESTING */}
      <section className="srx-sec dark" data-testid="conquesting">
        <div className="srx-wrap">
          <SecHead eyebrow="Competitor conquesting"
            title="Reach potential customers at your competitor's business"
            sub="SmartReach can target people in and around the places your competitors operate — so when they're deciding where to go, your offer is the one in front of them." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {[
              { icon: Target, t: "Show up where they already are", d: "Reach phones seen around competitor locations and the shopping areas nearby." },
              { icon: Ticket, t: "Give them a reason to switch", d: "Pair that audience with a first-visit offer or coupon they can redeem in your store." },
              { icon: BarChart3, t: "See who actually came in", d: "Track coupon redemptions and matched purchases from the customers you won over." },
            ].map((c) => (
              <div key={c.t} className="srx-ccard" data-testid={`conquest-${c.t.split(" ")[0].toLowerCase()}`}>
                <span className="srx-ico amber"><c.icon size={20} /></span>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 16, fontSize: 13.5, color: "var(--x-muted-dark)" }}>
            Audience availability depends on your area and campaign.{" "}
            <Link to="/competitor-targeting" className="srx-learn" data-testid="conquest-link" style={{ display: "inline-flex" }}>See how competitor targeting works <ArrowRight size={14} /></Link>
          </p>
        </div>
      </section>

      {/* D2b. SMART TARGETING SEARCH */}
      <section className="srx-sec light" data-testid="smart-search">
        <div className="srx-wrap">
          <SecHead eyebrow="Advanced setup · smart search"
            title="Find your exact audience — and see the reach in your area"
            sub="Two AI-powered searches inside every campaign's Advanced setup. Describe who you want, or what content you want to appear next to — we ground it to real data and show the estimated reach right in your target geography." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
            <div className="srx-ccard" data-testid="smart-search-audience">
              <span className="srx-ico ink"><Users size={20} /></span>
              <h3>Custom audience search</h3>
              <p>Type it in plain English — "homeowners 35-54 who dine out", "new movers", "pet owners" — and the AI matches real data-provider audiences, each showing the <strong>estimated reach in your selected geography</strong> (radius, ZIPs or metros) as you dial the map in.</p>
              <span className="srx-learn" style={{ display: "inline-flex" }}><Search size={14} /> Search &amp; see local reach</span>
            </div>
            <div className="srx-ccard" data-testid="smart-search-contextual">
              <span className="srx-ico amber"><Layers size={20} /></span>
              <h3>Contextual search</h3>
              <p>Get placement ideas auto-suggested from <strong>your product &amp; services</strong> — local news, sports, cooking, faith &amp; family and more — so your ads run next to the content your customers already read, scoped to your area. No cookies, no behavioral tracking required.</p>
              <span className="srx-learn" style={{ display: "inline-flex" }}><Sparkles size={14} /> Auto-suggested for your business</span>
            </div>
          </div>
          <p style={{ marginTop: 16, fontSize: 13.5, color: "var(--x-muted, #64748b)" }}>
            Reach figures are planning estimates based on your target area. Competitor, custom-audience and contextual targeting all live together under one Advanced setup — pick whichever fits the campaign.
          </p>
        </div>
      </section>

      {/* D3. WHY NOT JUST META / GOOGLE */}
      <section className="srx-sec light" data-testid="vs-meta-google">
        <div className="srx-wrap">
          <SecHead eyebrow="Meta &amp; Google — and then some"
            title="One workspace instead of juggling ad platforms"
            sub="Meta and Google are powerful, but each shows only its own inventory and is a separate login, budget and learning curve. SmartReach coordinates them together — and adds what they can't." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
            <div className="srx-ccard" data-testid="vs-alone">
              <span className="srx-badge-pop" style={{ marginLeft: 0, marginBottom: 10, background: "#eef1f5", color: "#5b6b85" }}>ON YOUR OWN</span>
              <h3>Meta and Google, run separately</h3>
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                {[
                  "Two dashboards, two budgets and two learning curves to manage",
                  "No local radio and no in-store coupon redemption",
                  "You interpret the numbers on your own",
                ].map((t) => (
                  <li key={t} style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#334155", fontSize: 13.5 }}>
                    <X size={16} color="#d1435b" style={{ flexShrink: 0, marginTop: 2 }} /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="srx-ccard" style={{ borderColor: "var(--x-amber)" }} data-testid="vs-smartreach">
              <span className="srx-badge-pop" style={{ marginLeft: 0, marginBottom: 10 }}>WITH SMARTREACH</span>
              <h3>Everything planned together</h3>
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                {[
                  "Meta, Google, ChatGPT Ads, Connected TV, digital billboards, radio and local offers — coordinated in one plan",
                  "Competitor-location audiences and in-store coupon redemption built in",
                  "Every result labeled with its evidence level, with expert help when you want it",
                ].map((t) => (
                  <li key={t} style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#334155", fontSize: 13.5 }}>
                    <Check size={16} color="#0f9d58" style={{ flexShrink: 0, marginTop: 2 }} /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="srx-note" style={{ marginTop: 18 }}>
            <Radio size={22} color="var(--x-amber)" style={{ flexShrink: 0 }} />
            <p>
              <strong style={{ color: "#fff" }}>Use the big platforms and the local edge.</strong> We run your Meta advertising for you and coordinate it with community radio and in-store offers — the local reach Meta and Google don't sell.
            </p>
          </div>
        </div>
      </section>

      {/* E. ONE CONCRETE CAMPAIGN EXAMPLE */}
      <section className="srx-sec dark" data-testid="example">
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">A concrete example</span>
            <h2>What a simple campaign looks like</h2>
          </div>
          <div className="srx-ccard" style={{ padding: 0, overflow: "hidden" }} data-testid="example-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 18px", background: "rgba(242,163,61,.08)", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
              <span style={{ color: "#fff", fontWeight: 700 }}><Store size={16} color="var(--x-amber)" style={{ verticalAlign: "-3px", marginRight: 8 }} />Neighborhood restaurant — weekday offer</span>
              <SampleTag>Illustrative campaign — not customer results</SampleTag>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 0 }}>
              {[
                ["Business goal", "Fill tables on slow weekday afternoons."],
                ["Offer & creative", "\u201c20% off, Tue–Thu, 4–6pm\u201d — one ad, one landing offer."],
                ["Eligible channels", "Meta Ads + a local offer. Two channels sized to the budget — not five."],
                ["Approval preview", "You approve the plan and creative before anything runs."],
              ].map(([k, v]) => (
                <div key={k} style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,.08)", borderRight: "1px solid rgba(255,255,255,.08)" }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--x-amber-dark)", fontWeight: 800, marginBottom: 6 }}>{k}</div>
                  <div style={{ color: "#dfe7f7", fontSize: 14 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Sample report</span>
                <SampleTag>Sample data</SampleTag>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
                {[
                  ["Sample spend", "$600", "Projected"],
                  ["Sample leads", "42", "Observed"],
                  ["Coupon redemptions", "28", "Observed"],
                  ["Matched sales", "$1,910", "Verified purchase"],
                ].map(([label, val, ev]) => (
                  <div key={label} style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.09)", borderRadius: 10, padding: 12 }}>
                    <div style={{ fontFamily: "var(--x-fdisplay)", fontSize: 24, fontWeight: 800, color: "#fff" }}>{val}</div>
                    <div style={{ fontSize: 12.5, color: "#dfe7f7", margin: "2px 0 8px" }}>{label}</div>
                    <SampleTag>{ev}</SampleTag>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--x-muted-dark)" }}>
                A recorded purchase doesn't by itself prove the campaign caused it. Visits and clicks are never added
                together to imply distinct customers. Availability and reporting depend on your campaign and connected data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* F. CHOOSE YOUR LEVEL OF HELP */}
      <section className="srx-sec light" data-testid="levels">
        <div className="srx-wrap">
          <SecHead eyebrow="Your call" title="Choose your level of help" />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {LEVELS.map((m) => (
              <div key={m.t} className="srx-ccard" data-testid={`level-${m.t.split(/[ -]/)[0].toLowerCase()}`}
                style={m.rec ? { borderColor: "var(--x-amber)" } : {}}>
                {m.rec && <span className="srx-badge-pop" style={{ marginLeft: 0, marginBottom: 10 }}>RECOMMENDED</span>}
                <h3>{m.t}</h3>
                <p>{m.d}</p>
              </div>
            ))}
          </div>
          <div className="srx-note" style={{ marginTop: 18 }}>
            <Wand2 size={22} color="var(--x-amber)" style={{ flexShrink: 0 }} />
            <p>
              <strong style={{ color: "#fff" }}>Easy and Pro change the interface</strong> — how much you see on screen.
              Your <strong style={{ color: "#fff" }}>service level</strong> determines the support and contractual work.
              Switching interface mode never changes your price, permissions or service level.
            </p>
          </div>
        </div>
      </section>

      {/* G. TRANSPARENT PRICING & EVIDENCE */}
      <section className="srx-sec dark" data-testid="pricing">
        <div className="srx-wrap">
          <SecHead eyebrow="Transparent pricing" title="Clear costs, shown as separate lines" />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1fr)", gap: 20 }}>
            <div className="srx-ccard" data-testid="pricing-meta">
              <span className="srx-ico amber"><Sparkles size={20} /></span>
              <h3>Meta Ads — optional add-on</h3>
              <p>
                A <strong style={{ color: "#fff" }}>{moduleFee}/client per month</strong> module fee plus
                {" "}<strong style={{ color: "#fff" }}>{mgmtPct}% of your actual Meta media spend</strong>. Both are
                set by your account and can be adjusted per client.
              </p>
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                {[
                  "Media spend is billed separately and paid to run your ads.",
                  "Subscription, media spend and management fee are always shown as separate lines.",
                  "No vague all-in number — you always see what each part costs.",
                ].map((t) => (
                  <li key={t} style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#dfe7f7", fontSize: 13.5 }}>
                    <Check size={16} color="#3fbf7f" style={{ flexShrink: 0, marginTop: 2 }} /> {t}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 16 }}>
                <Link to="/pricing" className="srx-btn-primary" data-testid="pricing-view-btn">View full plans &amp; pricing <ArrowRight size={16} /></Link>
              </div>
            </div>
            <div className="srx-ccard" data-testid="pricing-evidence">
              <span className="srx-ico ink"><ShieldCheck size={20} /></span>
              <h3>How we label results</h3>
              <p>Every number carries its evidence level, next to the figure — never a projection dressed up as a result.</p>
              <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                {EVIDENCE.map(([k, v]) => (
                  <div key={k} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, alignItems: "start" }}>
                    <SampleTag>{k}</SampleTag>
                    <span style={{ color: "#dfe7f7", fontSize: 13 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* H. FINAL INQUIRY CTA */}
      <section className="srx-footer-cta" data-testid="footer-cta">
        <div className="srx-wrap">
          <h2>Ready to see your plan?</h2>
          <p style={{ color: "rgba(255,255,255,.75)", maxWidth: 560, margin: "0 auto 22px", fontSize: 15 }}>
            Tell us about your business and goal. Our team will review it and follow up — no payment or account required.
          </p>
          <div className="srx-cta-row" style={{ justifyContent: "center" }}>
            <button className="srx-btn-primary" data-testid="footer-cta-primary" onClick={() => openInquiry()}>Request my marketing plan</button>
            <Link to="/pricing" className="srx-btn-secondary" data-testid="footer-cta-secondary">View plans &amp; pricing</Link>
          </div>
        </div>
      </section>

      <InquiryModal open={modalOpen} onClose={() => setModalOpen(false)} goal={goal} setGoal={setGoal} />
      <ShopifyCaseModal open={caseOpen} onClose={() => setCaseOpen(false)} onCta={() => { setCaseOpen(false); openInquiry("more_sales"); }} />
      <MovieCaseModal open={movieCaseOpen} onClose={() => setMovieCaseOpen(false)} onCta={() => { setMovieCaseOpen(false); openInquiry("promote_an_event"); }} />
      <RestaurantCaseModal open={restaurantCaseOpen} onClose={() => setRestaurantCaseOpen(false)} onCta={() => { setRestaurantCaseOpen(false); openInquiry("more_store_visits"); }} />
    </MarketingPage>
  );
}
